import { Link } from '@tanstack/react-router';
import {
  CpuIcon,
  UserCheckIcon,
  Workflow as WorkflowIcon,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { FOCUS_RING } from '@/shared/components/pillClasses';
import { identityDot } from '../identityFill';
import type { IdentitySlot, MapStepKind } from '../modelsLedger';

/**
 * The pill: the board header pill's shape and type size (`HEADER_PILL_BOX`), but
 * wrapping, because a step's name can be a sentence, and at normal weight, because a
 * step is a fact here and not a control.
 */
const CHIP =
  'inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-normal break-words transition-opacity duration-[var(--motion-duration-base)] motion-reduce:transition-none';

const KIND_ICON: Partial<Record<MapStepKind, LucideIcon>> = {
  default: CpuIcon,
  approval: UserCheckIcon,
  linked: WorkflowIcon,
};

/** What a screen reader hears after the step's name. */
function kindText(kind: MapStepKind, profileName: string | undefined): string {
  switch (kind) {
    case 'profile':
      return ` on ${profileName ?? 'its profile'}`;
    case 'default':
      return ' on the default model';
    case 'approval':
      return ', approval';
    case 'linked':
      return ', linked workflow';
  }
}

export interface StepChipLink {
  workspaceId: string;
  workflowKey: string;
  stepId: string;
}

/**
 * One workflow step, drawn the same in both Models views (spec 234 US2): a neutral
 * pill. An AI step on a profile leads with the profile's dot; one on the default
 * model with the chip glyph; an approval or a linked workflow with its icon, in muted
 * text, because it runs no model of its own. Unpublished is said in words. With
 * `link`, the chip opens the workflow on that step.
 */
export function StepChip({
  kind,
  name,
  slot,
  idle = false,
  profileName,
  profileKey,
  unpublished = false,
  dimmed = false,
  link,
}: {
  kind: MapStepKind;
  name: string;
  /** The profile's identity, for a `profile` step. */
  slot?: IdentitySlot;
  /** The profile did not run in the period: its dot is a ring (spec 252). */
  idle?: boolean;
  profileName?: string;
  /** The profile's key, carried for the legend's highlight. */
  profileKey?: string;
  unpublished?: boolean;
  dimmed?: boolean;
  link?: StepChipLink;
}) {
  const Icon = KIND_ICON[kind];
  const dot =
    kind === 'profile' && slot !== undefined
      ? identityDot({ slot, idle })
      : null;
  const quiet = kind === 'approval' || kind === 'linked';
  const className = cn(
    CHIP,
    quiet ? 'text-muted-foreground' : 'text-foreground',
    link && `touch-target hover:bg-accent ${FOCUS_RING}`,
    dimmed && 'opacity-30',
  );
  const content = (
    <>
      {dot ? (
        <span
          aria-hidden="true"
          data-identity-dot=""
          className={cn('size-2 shrink-0 rounded-full', dot)}
        />
      ) : Icon ? (
        <Icon
          aria-hidden="true"
          className="text-muted-foreground size-3.5 shrink-0"
          strokeWidth={2}
        />
      ) : null}
      {name}
      <span className="sr-only">{kindText(kind, profileName)}</span>
      {unpublished ? (
        <span className="text-muted-foreground">· unpublished</span>
      ) : null}
    </>
  );
  const data = {
    'data-step-kind': kind,
    'data-profile': kind === 'profile' ? profileKey : undefined,
    title: kind === 'default' ? 'Runs on the default model' : undefined,
  };
  return link && !unpublished ? (
    <Link
      to="/workspaces/$workspaceId/workflows/$workflowKey"
      params={{
        workspaceId: link.workspaceId,
        workflowKey: link.workflowKey,
      }}
      search={{ step: link.stepId }}
      className={className}
      {...data}
    >
      {content}
    </Link>
  ) : (
    <span className={className} {...data}>
      {content}
    </span>
  );
}
